import axios from "axios";
import { APIError } from "marvin-auth-kit";

import { useMutation, useQuery, useQueryClient } from "react-query";

import { Indicator, IndicatorFormData } from "types";
import { IndicatorLayersResponse } from "types/backend";

import { deserializeIndicator, serializeIndicator } from "utils/IndicatorUtils";
import { useSearchParams } from "react-router-dom";

export const useMask = (config: string, id: number | "") => {
  const fetch = async (): Promise<Indicator> => {
    const response = await axios.get<IndicatorLayersResponse>(
      `/api/${config}/settings/masks/${id}`
    );
    return deserializeIndicator(response.data, []);
  };

  return useQuery(["mask", id, config], fetch);
};

export const useEnabledMasks = (masks: Indicator[]) => {
  const [params] = useSearchParams();
  const disabledMasks = params.getAll("masks");

  async function fetch() {
    return Promise.resolve(
      masks.filter((mask) => disabledMasks.indexOf(mask.id.toString()) === -1)
    );
  }

  return useQuery(["masks", "enabled", disabledMasks.join(",")], fetch);
};

export const useUpdateMask = (config: string) => {
  const queryClient = useQueryClient();
  const update = async (mask: Indicator) => {
    const response = await axios.put<IndicatorLayersResponse>(
      `/api/${config}/settings/masks/${mask.id}`,
      serializeIndicator(mask)
    );
    return response.data;
  };
  return useMutation<IndicatorLayersResponse, APIError[], Indicator, unknown>(
    update,
    {
      onSuccess: (mask) => {
        queryClient.invalidateQueries(["settings", config]);
        queryClient.invalidateQueries(["mask", mask.id, config]);
      },
    }
  );
};

export const useAddMask = (config: string) => {
  const queryClient = useQueryClient();
  const update = async (mask: IndicatorFormData) => {
    const response = await axios.post<IndicatorLayersResponse>(
      `/api/${config}/settings/masks`,
      serializeIndicator(mask)
    );
    return response.data;
  };
  return useMutation<
    IndicatorLayersResponse,
    APIError[],
    IndicatorFormData,
    unknown
  >(update, {
    onSuccess: () => {
      queryClient.invalidateQueries(["settings", config]);
    },
  });
};

export const useDeleteMask = (config: string) => {
  const queryClient = useQueryClient();
  const deleteMask = async (id: number) => {
    await axios.delete<void>(`/api/${config}/settings/masks/${id}`);
  };
  return useMutation<void, APIError[], number, unknown>(deleteMask, {
    onSuccess: () => {
      queryClient.invalidateQueries(["settings", config]);
    },
  });
};
